import React from 'react';
import { UtensilsCrossed, Waves, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { culinaryServices, equipment } from '../data/mock';

export const Services = () => {
  return (
    <section id="services" className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-orange-50/50"></div>
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Culinary Services */}
        <div className="mb-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-4 bg-orange-100 text-orange-700 hover:bg-orange-200">
              <UtensilsCrossed className="w-4 h-4 mr-1" />
              Prestations Culinaires
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Savourez l'excellence en 
              <span className="bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent"> mer</span>
            </h2>
            <p className="text-lg text-gray-600">
              Des mets raffinés pour sublimer votre expérience maritime
            </p>
          </div>

          {/* Culinary Image */}
          <div className="max-w-5xl mx-auto mb-12 rounded-3xl overflow-hidden shadow-2xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_marine-reserve-app/artifacts/elzb4xp5_IMG_3823.jpeg"
              alt="Prestations culinaires The Myli Boat"
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Culinary Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {culinaryServices.map((service) => (
              <Card 
                key={service.id}
                className="group hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 hover:border-orange-500 bg-gradient-to-br from-white to-orange-50/30"
              >
                <CardHeader className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="text-3xl">{getServiceEmoji(service.name)}</span>
                  </div>
                  <CardTitle className="text-lg">{service.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Equipment Section */}
        <div className="pt-12 border-t-2 border-gray-200">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
              <Waves className="w-4 h-4 mr-1" />
              Équipements
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Tout pour votre 
              <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent"> confort</span>
            </h2>
            <p className="text-lg text-gray-600">
              Matériel de qualité inclus dans toutes nos prestations
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {equipment.map((item) => (
              <Card 
                key={item.id}
                className="group hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 hover:border-blue-500 bg-gradient-to-br from-white to-blue-50/30"
              >
                <CardHeader className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="text-3xl">{getEquipmentEmoji(item.name)}</span>
                  </div>
                  <CardTitle className="text-lg">{item.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Special Note for Sunset */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="relative z-10 flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Sparkles className="w-12 h-12" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Expérience Sunset Premium</h3>
                  <p className="text-white/90 text-lg leading-relaxed">
                    Pour les sorties Sunset, profitez d'un équipement spécial : matelas en mousse moelleux, 
                    coussins douillets et plaids pour vous installer confortablement à l'avant du bateau 
                    et contempler le coucher de soleil dans un confort absolu.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Helper functions for emojis
const getServiceEmoji = (name) => {
  const emojiMap = {
    'Petits Fours': '🥐',
    'Verrines': '🍷',
    'Plateaux de Charcuterie': '🧀',
    'Barbecue': '🔥',
    'Plateaux de Fruits': '🍎'
  };
  return emojiMap[name] || '🍽️';
};

const getEquipmentEmoji = (name) => {
  const emojiMap = {
    'Bouées': '🛟',
    'Paddle': '🏄',
    'Masques de plongée': '🤿',
    'Équipement Sunset': '🛋️'
  };
  return emojiMap[name] || '⚓';
};
